// Lightweight client-side bus so the Recovery Journey stats on /profile can
// refresh the moment the member records a new entry, marks an article read,
// reserves an event, etc. — without a full page reload.
//
// SSR-safe: every API guards `typeof window`. Cross-tab updates are picked up
// via `localStorage` writes from the same key.

export type ActivityKind =
  | "journal"
  | "dream"
  | "mood"
  | "exercise"
  | "article"
  | "event"
  | "favourite"
  | "unknown";

const EVENT_NAME = "rt:activity-change";
const STORAGE_KEY = "rt:activity-change:tick";

export function notifyActivityChange(kind: ActivityKind = "unknown"): void {
  if (typeof window === "undefined") return;
  const detail = { kind, at: Date.now() };
  try {
    window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail }));
  } catch {
    // ignore — non-browser environment
  }
  // Cross-tab signal. Writing the same value won't fire `storage`, so vary it.
  try {
    window.localStorage.setItem(STORAGE_KEY, String(detail.at));
  } catch {
    // storage may be unavailable (private mode, quota); the in-tab event still fires.
  }
}

export function subscribeActivityChange(
  cb: (kind: ActivityKind) => void,
): () => void {
  if (typeof window === "undefined") return () => {};
  const onCustom = (e: Event) => {
    const detail = (e as CustomEvent<{ kind?: ActivityKind }>).detail;
    cb(detail?.kind ?? "unknown");
  };
  const onStorage = (e: StorageEvent) => {
    if (e.key === STORAGE_KEY) cb("unknown");
  };
  window.addEventListener(EVENT_NAME, onCustom);
  window.addEventListener("storage", onStorage);
  return () => {
    window.removeEventListener(EVENT_NAME, onCustom);
    window.removeEventListener("storage", onStorage);
  };
}
